// /managers/ConvoManager/ConvoPruning.jsx
// Token estimation constants
const AVG_CHARS_PER_TOKEN = 3.5;
const TOKEN_OVERHEAD_PER_FILE = 50;
const TOKEN_OVERHEAD_PER_IMAGE = 350;
export const MIN_TARGET_INPUT_TOKENS = 1000;

/* ========================
 * Global Defaults Management - Handles user's preferred memory settings
 * Persists to localStorage for consistent experience
 * ======================== */
const GLOBAL_DEFAULTS_KEY = 'shelbula_memory_defaults';

const getStoredDefaults = () => {
  try {
    const stored = localStorage.getItem(GLOBAL_DEFAULTS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const storeDefaults = (defaults) => {
  try {
    localStorage.setItem(GLOBAL_DEFAULTS_KEY, JSON.stringify(defaults));
  } catch (error) {
    console.warn('Failed to store memory defaults:', error);
  }
};

export const getGlobalMemoryDefaults = () => {
  const stored = getStoredDefaults();
  return {
    defaultMemorySize: stored.defaultMemorySize || 20,
    absoluteMax: 100
  };
};

export const updateGlobalMemoryDefaults = (updates) => {
  const current = getStoredDefaults();
  const newDefaults = { ...current, ...updates };
  storeDefaults(newDefaults);
};

/* ========================
 * estimateCurrentInputTokens - Estimates token count for current input
 * Used by InputArea to provide real-time token estimates
 * ======================== */
export const estimateCurrentInputTokens = (message = '', files = {}) => {
  const content = message || '';
  const filesArray = Array.isArray(files) ? files : Object.values(files || {});
  
  // Text tokens from message content
  const textTokens = Math.ceil(content.length / AVG_CHARS_PER_TOKEN);
  
  // File tokens
  let fileTokens = 0;
  let fileCount = 0;
  
  filesArray.forEach(file => {
    fileCount++;
    
    if (file.type?.startsWith('image/')) {
      // Images have fixed token cost
      fileTokens += TOKEN_OVERHEAD_PER_IMAGE;
    } else if (file.content && typeof file.content === 'string') {
      // Text files: content + overhead
      fileTokens += Math.ceil(file.content.length / AVG_CHARS_PER_TOKEN) + TOKEN_OVERHEAD_PER_FILE;
    } else {
      // Unknown files: just overhead
      fileTokens += TOKEN_OVERHEAD_PER_FILE;
    }
  });
  
  return {
    content,
    fileCount,
    textTokens,
    fileTokens,
    estimatedTokens: textTokens + fileTokens
  };
};

/* ========================
 * estimateMessageTokens - Estimates tokens for a single message
 * Includes content, files, and images
 * ======================== */
export const estimateMessageTokens = (message) => {
  if (!message || message.type === 'summary') return 0;
  
  let tokens = 0;
  
  // Content tokens
  const content = message.rawContent || message.content || '';
  tokens += Math.ceil(content.length / AVG_CHARS_PER_TOKEN);
  
  // File tokens
  if (message.files && Array.isArray(message.files)) {
    message.files.forEach(file => {
      if (file.content && typeof file.content === 'string') {
        tokens += Math.ceil(file.content.length / AVG_CHARS_PER_TOKEN) + TOKEN_OVERHEAD_PER_FILE;
      } else {
        tokens += TOKEN_OVERHEAD_PER_FILE;
      }
    });
  }
  
  // Image tokens
  if (message.images && Array.isArray(message.images)) {
    tokens += message.images.length * TOKEN_OVERHEAD_PER_IMAGE;
  }
  
  return tokens;
};

/* ========================
 * estimateConversationTokens - Estimates total tokens for conversation messages
 * Used by PruningSlider for context calculations
 * ======================== */
export const estimateConversationTokens = (messages = []) => {
  if (!Array.isArray(messages)) return 0;
  
  return messages.reduce((total, message) => {
    return total + estimateMessageTokens(message);
  }, 0);
};

/* ========================
 * pruneMessagesForMemory - Prunes messages based on memory size setting
 * Keeps the most recent messages up to the memory limit
 * Used by ConvoManager for all memory-based pruning
 * ======================== */
export const pruneMessagesForMemory = (messages, maxMessages = 30) => {
  if (!Array.isArray(messages) || messages.length <= maxMessages) {
    return messages;
  }
  
  // Keep the most recent messages up to the memory limit
  return messages.slice(-maxMessages);
};

/* ========================
 * pruneMessagesForTargetInputTokens - Prunes messages to fit within token limit
 * Keeps most recent messages that fit within the target token count
 * ======================== */
export const pruneMessagesForTargetInputTokens = (messages = [], targetTokens = 75000, inputTokens = 0) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return messages;
  }
  
  const availableTokens = Math.max(0, targetTokens - inputTokens);
  let currentTokens = 0;
  const prunedMessages = [];
  
  // Work backwards from most recent messages
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    const messageTokens = estimateMessageTokens(message);
    
    if (currentTokens + messageTokens <= availableTokens) {
      prunedMessages.unshift(message);
      currentTokens += messageTokens;
    } else {
      // Stop if adding this message would exceed the limit
      break;
    }
  }
  
  return prunedMessages;
};

/* ========================
 * calculateVisibleMessageIndex - Calculates which message index should be visible
 * Used for scroll positioning based on pruning
 * ======================== */
export const calculateVisibleMessageIndex = (allMessages = [], prunedMessages = []) => {
  if (!Array.isArray(allMessages) || !Array.isArray(prunedMessages)) {
    return 0;
  }
  
  if (prunedMessages.length === 0) {
    return 0;
  }
  
  // Find the index of the first pruned message in the original array
  const firstPrunedMessage = prunedMessages[0];
  if (!firstPrunedMessage) {
    return 0;
  }
  
  const index = allMessages.findIndex(msg => msg.id === firstPrunedMessage.id);
  return Math.max(0, index);
};

/* ========================
 * getOptimalMemorySize - Suggests optimal memory size based on user preferences
 * Now uses global defaults instead of complex calculations
 * ======================== */
export const getOptimalMemorySize = (messages = [], userTier = 'Free') => {
  if (userTier === 'Free') {
    return 10; 
  } else {
    const defaults = getGlobalMemoryDefaults();
    return defaults.defaultMemorySize;
  }
};

/* ========================
 * ensureTokenFields - Ensures conversation has proper token management fields
 * Derives missing values from model data if needed
 * ======================== */
export const ensureTokenFields = (conversation) => {
  let targetTokens = conversation.targetInputTokens;
  let modelCtx = conversation.modelMaxContextWindow;
  let modelOut = conversation.modelMaxOutputTokens;
  const { botManager } = require('@/managers/BotManager'); // Delayed import

  if (targetTokens === undefined || modelCtx === undefined || modelOut === undefined) {
    const modelIdToUse = conversation.modelId || botManager.getState().currentModel?.id;
    const model = botManager.getState().availableModels.find(m => m.id === modelIdToUse) || botManager.getState().currentModel;
    
    if (model && typeof model.maxContextWindow === 'number' && typeof model.maxTokens === 'number') {
      modelCtx = modelCtx === undefined ? model.maxContextWindow : modelCtx;
      modelOut = modelOut === undefined ? model.maxTokens : modelOut;
      targetTokens = targetTokens === undefined ? Math.max(MIN_TARGET_INPUT_TOKENS, modelCtx - modelOut) : targetTokens;
    } else {
      targetTokens = targetTokens || MIN_TARGET_INPUT_TOKENS;
      modelCtx = modelCtx || 0;
      modelOut = modelOut || 0;
    }
  }

  return {
    targetInputTokens: targetTokens,
    modelMaxContextWindow: modelCtx,
    modelMaxOutputTokens: modelOut
  };
};

/* ========================
 * analyzeConversationFiles - Extracts and analyzes files/images from messages
 * Determines context status, token cost, and relative position.
 * ======================== */
export const analyzeConversationFiles = (allMessages = [], limitedMessageIdsSet = new Set()) => {
  const analyzedAttachments = [];
  const totalMessages = allMessages.length;

  allMessages.forEach((msg, index) => {
    if (msg.role !== 'user' && msg.role !== 'assistant') return; 

    const processAttachment = (attachment, type) => {
      let tokenCost = 0;
      if (type === 'image') {
        tokenCost = TOKEN_OVERHEAD_PER_IMAGE;
      } else { 
        tokenCost = TOKEN_OVERHEAD_PER_FILE;
        if (attachment.content && typeof attachment.content === 'string') {
          tokenCost += Math.ceil(attachment.content.length / AVG_CHARS_PER_TOKEN);
        }
      }

      const turnsAgo = totalMessages - 1 - index;
      let relativePositionText = '';
      if (turnsAgo === 0) {
        relativePositionText = "(current msg)";
      } else if (turnsAgo === 1) {
        relativePositionText = "(1 msg ago)";
      } else {
        relativePositionText = `(${turnsAgo} msgs ago)`;
      }
      
      analyzedAttachments.push({
        attachmentId: attachment.id,
        messageId: msg.id,
        name: attachment.name || 'Untitled',
        type: type, 
        size: attachment.size,
        messageTimestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
        messageIndex: index,
        isInContext: limitedMessageIdsSet.has(msg.id),
        relativePositionText,
        tokenCost,
      });
    };

    if (msg.files && Array.isArray(msg.files)) {
      // Filter out audio files - they are temporary voice recordings sent for transcription
      // and should not appear in the file management UI
      msg.files
        .filter(file => !file.type?.startsWith('audio/'))
        .forEach(file => processAttachment(file, 'file'));
    }
    if (msg.images && Array.isArray(msg.images)) {
      msg.images.forEach(image => processAttachment(image, 'image'));
    }
  });

  analyzedAttachments.sort((a, b) => b.messageTimestamp - a.messageTimestamp);
  
  return analyzedAttachments;
};